/**
 * DueDateQuickFilter - Filtro rápido de Prazo (Due Date) padronizado para o módulo Tarefas
 *
 * Exibe um popover com presets rápidos:
 *   - Vencido
 *   - Vencimento Hoje
 *   - Vencimento esta Semana
 *   - Vencimento Próxima Semana
 *   - Vencimento Mês que Vem
 *   - Sem Prazo
 *   - Personalizada (abre calendário De/Até)
 *
 * Aplicação centralizada para garantir DRY entre Lista, Kanban e Dashboard.
 *
 * @version 1.0.0
 * RELEASE: 2026-04-18 v1.0.0 - Criação inicial
 */

import { useMemo, useState } from 'react';
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  addWeeks,
  startOfMonth,
  endOfMonth,
  addMonths,
  subDays,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarDays, X, Check, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/** Identificador do preset selecionado (ou 'custom' para faixa manual) */
export type DueDatePreset =
  | 'overdue'
  | 'today'
  | 'this_week'
  | 'next_week'
  | 'next_month'
  | 'no_due_date'
  | 'custom'
  | null;

/** Payload retornado em cada mudança */
export interface DueDateQuickFilterValue {
  from: Date | undefined;
  to: Date | undefined;
  noDueDate: boolean;
  preset: DueDatePreset;
}

interface DueDateQuickFilterProps {
  /** Data inicial (faixa custom ou preset com from) */
  from: Date | undefined;
  /** Data final (faixa custom ou preset com to) */
  to: Date | undefined;
  /** Se TRUE, filtra apenas tarefas sem prazo (due_date IS NULL) */
  noDueDate?: boolean;
  /** Preset atualmente selecionado (apenas para destaque visual; opcional) */
  preset?: DueDatePreset;
  /** Callback de mudança */
  onChange: (value: DueDateQuickFilterValue) => void;
  /** Tamanho/altura do botão. Use 'h-[22px]' (dashboard), 'h-7' (kanban) ou 'h-6 sm:h-8' (lista) */
  triggerClassName?: string;
  /** Alinhamento do popover */
  align?: 'start' | 'center' | 'end';
}

/** Lista de presets exibidos no popover (na ordem da imagem) */
const PRESET_OPTIONS: { key: Exclude<DueDatePreset, null | 'custom'>; label: string }[] = [
  { key: 'overdue', label: 'Vencido' },
  { key: 'today', label: 'Vencimento Hoje' },
  { key: 'this_week', label: 'Vencimento esta Semana' },
  { key: 'next_week', label: 'Vencimento Próxima Semana' },
  { key: 'next_month', label: 'Vencimento Mês que Vem' },
  { key: 'no_due_date', label: 'Sem Prazo' },
];

/**
 * Calcula o intervalo de datas correspondente a cada preset.
 * Retorna `noDueDate=true` exclusivamente para o preset "Sem Prazo".
 */
function computePresetRange(preset: Exclude<DueDatePreset, null | 'custom'>): DueDateQuickFilterValue {
  const today = new Date();
  switch (preset) {
    case 'overdue':
      // Vencido = prazo expirado (até ontem 23:59:59)
      return { from: undefined, to: endOfDay(subDays(today, 1)), noDueDate: false, preset };
    case 'today':
      return { from: startOfDay(today), to: endOfDay(today), noDueDate: false, preset };
    case 'this_week': {
      const ws = startOfWeek(today, { weekStartsOn: 1 });
      const we = endOfWeek(today, { weekStartsOn: 1 });
      return { from: ws, to: we, noDueDate: false, preset };
    }
    case 'next_week': {
      const next = addWeeks(today, 1);
      const ws = startOfWeek(next, { weekStartsOn: 1 });
      const we = endOfWeek(next, { weekStartsOn: 1 });
      return { from: ws, to: we, noDueDate: false, preset };
    }
    case 'next_month': {
      const next = addMonths(today, 1);
      return { from: startOfMonth(next), to: endOfMonth(next), noDueDate: false, preset };
    }
    case 'no_due_date':
      return { from: undefined, to: undefined, noDueDate: true, preset };
  }
}

/**
 * Componente principal — filtro rápido de Prazo
 */
export function DueDateQuickFilter({
  from,
  to,
  noDueDate = false,
  preset = null,
  onChange,
  triggerClassName,
  align = 'start',
}: DueDateQuickFilterProps) {
  // Controla a abertura do popover (para fechar ao selecionar)
  const [open, setOpen] = useState(false);
  // Controla se está exibindo o calendário (modo Personalizada) ou a lista de presets
  const [showCustom, setShowCustom] = useState(false);

  /** Indica se há algum filtro ativo (gera label dinâmico + cor de borda) */
  const hasValue = !!(from || to || noDueDate);

  /** Texto do botão refletindo o preset/intervalo ativo */
  const buttonLabel = useMemo(() => {
    if (noDueDate) return 'Prazo: Sem Prazo';
    if (preset && preset !== 'custom' && preset !== null) {
      const found = PRESET_OPTIONS.find((p) => p.key === preset);
      if (found) return `Prazo: ${found.label}`;
    }
    if (from && to) return `Prazo: ${format(from, 'dd/MM')} - ${format(to, 'dd/MM')}`;
    if (from) return `Prazo: ≥ ${format(from, 'dd/MM')}`;
    if (to) return `Prazo: ≤ ${format(to, 'dd/MM')}`;
    return 'Prazo';
  }, [from, to, noDueDate, preset]);

  /** Aplica um preset, fecha o popover e dispara onChange */
  const applyPreset = (key: Exclude<DueDatePreset, null | 'custom'>) => {
    onChange(computePresetRange(key));
    setShowCustom(false);
    setOpen(false);
  };

  /** Limpa todo o filtro */
  const clear = () => {
    onChange({ from: undefined, to: undefined, noDueDate: false, preset: null });
    setShowCustom(false);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        // Ao fechar, voltar para a lista de presets
        if (!o) setShowCustom(false);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-1.5 shrink-0 border-0 bg-background rounded-full',
            triggerClassName,
            hasValue && 'border-primary text-primary',
          )}
        >
          <CalendarDays className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          {buttonLabel}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-64 p-2" align={align}>
        {!showCustom ? (
          // ============ Modo Presets (lista vertical) ============
          <div className="flex flex-col gap-0.5">
            {PRESET_OPTIONS.map((opt) => {
              const isActive =
                (opt.key === 'no_due_date' && noDueDate) || (preset === opt.key && !noDueDate);
              return (
                <Button
                  key={opt.key}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-8 justify-start text-xs font-normal px-2',
                    isActive && 'border border-primary text-primary',
                  )}
                  onClick={() => applyPreset(opt.key)}
                >
                  {isActive && <Check className="h-3 w-3 mr-1" />}
                  {opt.label}
                </Button>
              );
            })}

            <Separator className="my-1" />

            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-8 justify-start text-xs font-normal px-2',
                preset === 'custom' && 'border border-primary text-primary',
              )}
              onClick={() => setShowCustom(true)}
            >
              {preset === 'custom' && <Check className="h-3 w-3 mr-1" />}
              Personalizada
            </Button>

            {hasValue && (
              <>
                <Separator className="my-1" />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 justify-start text-xs font-normal px-2 text-muted-foreground"
                  onClick={clear}
                >
                  <X className="h-3 w-3 mr-1" /> Limpar prazo
                </Button>
              </>
            )}
          </div>
        ) : (
          // ============ Modo Personalizada (calendário De/Até) ============
          <div className="space-y-2 p-1">
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-1 text-xs gap-1"
                onClick={() => setShowCustom(false)}
              >
                <ChevronLeft className="h-3 w-3" /> Voltar
              </Button>
              {(from || to) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-muted-foreground"
                  onClick={clear}
                >
                  <X className="h-3 w-3 mr-1" /> Limpar
                </Button>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <CalendarDays className="h-3 w-3" />
                Prazo (De / Até)
              </Label>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 justify-start text-xs font-normal"
                    >
                      {from ? format(from, 'dd/MM/yy') : 'De'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" side="bottom">
                    <Calendar
                      mode="single"
                      selected={from}
                      onSelect={(d) =>
                        onChange({ from: d, to, noDueDate: false, preset: 'custom' })
                      }
                      className="p-3 pointer-events-auto"
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 flex-1 justify-start text-xs font-normal"
                    >
                      {to ? format(to, 'dd/MM/yy') : 'Até'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" side="bottom">
                    <Calendar
                      mode="single"
                      selected={to}
                      onSelect={(d) =>
                        onChange({ from, to: d, noDueDate: false, preset: 'custom' })
                      }
                      className="p-3 pointer-events-auto"
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
